import { useState } from 'react';
import type { TimePeriod, TimeJumpData } from '@/types/timeProgression';
import { useTimePeriodRestart } from './useTimePeriodRestart';
import { devLog } from '@/lib/devPerfLog';

// Environment-based kill switch for automatic time jump detection
// Set VITE_DISABLE_TIME_JUMP=true to disable automatic time jumps
const DISABLE_TIME_JUMP = import.meta.env.VITE_DISABLE_TIME_JUMP === 'true';

export function useTimeJumpModal() {
  const [showTimeJumpModal, setShowTimeJumpModal] = useState(false);
  const [timeJumpSummary, setTimeJumpSummary] = useState<string>('');
  const [timeJumpDuration, setTimeJumpDuration] = useState<TimeJumpData | null>(null);
  const [shownSummaryForPeriod, setShownSummaryForPeriod] = useState<string | null>(null);
  const [nextPeriod, setNextPeriod] = useState<TimePeriod | null>(null);
  const [restartCount, setRestartCount] = useState(0);
  const { restartTimePeriod, getRestartCount } = useTimePeriodRestart();

  const checkAndShowTimeJump = async (
    financialData: { current_time_period_id: string | null } | null,
    nextPeriodData: TimePeriod | null
  ) => {
    // Environment-configurable kill switch for automatic time jump detection
    if (DISABLE_TIME_JUMP) {
      devLog('TimeJumpModal', 'Automatic time jump disabled via VITE_DISABLE_TIME_JUMP');
      return;
    }
    
    if (!financialData || !nextPeriodData) return;

    const hasValidSummary = nextPeriodData.summary_text && nextPeriodData.summary_text.trim().length > 0;
    
    devLog('TimeJumpModal', 'Time jump check', {
      playerCurrentPeriod: financialData.current_time_period_id,
      nextPeriod: nextPeriodData.id,
      nextPeriodName: nextPeriodData.period_name,
      hasValidSummary,
      alreadyShownForPeriod: shownSummaryForPeriod === nextPeriodData.id
    });
    
    // Show modal if we have a valid summary AND we haven't shown it for this period yet
    if (hasValidSummary && shownSummaryForPeriod !== nextPeriodData.id) {
      devLog('TimeJumpModal', 'Showing time jump modal for:', nextPeriodData.period_name);
      setTimeJumpSummary(nextPeriodData.summary_text);
      setNextPeriod(nextPeriodData); // Store the next period data
      
      // Get restart count for this period
      const count = await getRestartCount(nextPeriodData.id);
      setRestartCount(count);
      
      // Set jump duration if available
      if (nextPeriodData.jump_duration_value && nextPeriodData.jump_duration_unit) {
        setTimeJumpDuration({
          value: nextPeriodData.jump_duration_value,
          unit: nextPeriodData.jump_duration_unit
        });
      } else {
        setTimeJumpDuration(null);
      }
      
      setShowTimeJumpModal(true);
      setShownSummaryForPeriod(nextPeriodData.id);
    }
  };

  const acknowledgeTimeJump = (onTimeAdvance?: (duration: { value: number; unit: string }) => void) => {
    // Advance game time if duration is set and callback provided
    if (timeJumpDuration && onTimeAdvance) {
      onTimeAdvance(timeJumpDuration);
    }
    
    setShowTimeJumpModal(false);
    setTimeJumpSummary('');
    setTimeJumpDuration(null);
    setNextPeriod(null);
  };

  const handleRestart = async () => {
    if (nextPeriod) {
      const success = await restartTimePeriod(nextPeriod.id, 'User requested restart from time jump modal');
      if (success) {
        setShowTimeJumpModal(false);
        setTimeJumpSummary('');
        setTimeJumpDuration(null);
        setNextPeriod(null);
      }
    }
  };

  const resetShownSummary = () => {
    setShownSummaryForPeriod(null);
  };

  return {
    showTimeJumpModal,
    timeJumpSummary,
    timeJumpDuration,
    nextPeriod,
    restartCount,
    checkAndShowTimeJump,
    acknowledgeTimeJump,
    handleRestart,
    resetShownSummary
  };
}